import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { CircularProgressbar, buildStyles } from 'react-circular-progressbar';
import 'react-circular-progressbar/dist/styles.css';
import { CheckIcon, XMarkIcon } from '@heroicons/react/24/solid';

const questionVariants = {
    initial: { opacity: 0, x: 500 },
    animate: { opacity: 1, x: 0 },
    exit: { opacity: 0, x: -500 },
};

const QuizzesPage = () => {
    const [quizzes, setQuizzes] = useState([]);
    const [currentQuizIndex, setCurrentQuizIndex] = useState(0);
    const [selectedAnswer, setSelectedAnswer] = useState(null);
    const [isAnswered, setIsAnswered] = useState(false);
    const [score, setScore] = useState(0);
    const [showResult, setShowResult] = useState(false);

    useEffect(() => {
        fetch('/quiz.json')
            .then(response => response.json())
            .then(data => setQuizzes(data));
    }, []);

    const handleAnswerSelection = (option) => {
        if (isAnswered) return;
        setSelectedAnswer(option);
    };

    const handleCheckAnswer = () => {
        setIsAnswered(true);
        if (selectedAnswer === quizzes[currentQuizIndex].answer) {
            setScore(score + 1);
        }
    };

    const handleNextQuestion = () => {
        setIsAnswered(false);
        setSelectedAnswer(null);
        if (currentQuizIndex < quizzes.length - 1) {
            setCurrentQuizIndex(currentQuizIndex + 1);
        } else {
            setShowResult(true);
        }
    };

    const handleRestartQuiz = () => {
        setCurrentQuizIndex(0);
        setSelectedAnswer(null);
        setIsAnswered(false);
        setScore(0);
        setShowResult(false);
    };

    const getButtonClass = (option) => {
        const correctAnswer = quizzes[currentQuizIndex].answer;
        if (isAnswered) {
            if (option === correctAnswer) {
                return 'bg-green-100 border-green-500 text-green-800'; // Correct answer
            }
            if (option === selectedAnswer && option !== correctAnswer) {
                return 'bg-red-100 border-red-500 text-red-800'; // Incorrectly selected
            }
        }
        if (selectedAnswer === option) {
            return 'bg-indigo-100 border-indigo-500'; // Selected
        }
        return 'bg-white hover:bg-gray-50'; // Default
    };

    if (quizzes.length === 0) {
        return <div className="text-center p-10">Loading Quiz...</div>;
    }

    if (showResult) {
        const percentage = Math.round((score / quizzes.length) * 100);
        return (
            <motion.div
                className="max-w-2xl mx-auto my-10 p-8 bg-white rounded-xl shadow-2xl text-center"
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
            >
                <h2 className="text-3xl font-bold text-gray-800">Quiz Complete!</h2>
                <div className="w-48 h-48 mx-auto my-8">
                    <CircularProgressbar
                        value={percentage}
                        text={`${percentage}%`}
                        styles={buildStyles({
                            pathColor: percentage > 70 ? '#10B981' : percentage > 40 ? '#F59E0B' : '#EF4444',
                            textColor: '#1F2937',
                            trailColor: '#E5E7EB',
                        })}
                    />
                </div>
                <p className="text-xl text-gray-600">
                    You answered <span className="font-bold text-indigo-600">{score}</span> out of <span className="font-bold text-indigo-600">{quizzes.length}</span> questions correctly.
                </p>
                <button
                    onClick={handleRestartQuiz}
                    className="mt-8 w-full bg-indigo-600 text-white py-3 rounded-lg font-semibold hover:bg-indigo-700 transition-colors"
                >
                    Restart Quiz
                </button>
            </motion.div>
        );
    }

    const currentQuiz = quizzes[currentQuizIndex];
    const progressPercentage = ((currentQuizIndex + 1) / quizzes.length) * 100;

    return (
        <div className="max-w-2xl mx-auto my-10">
            {/* Progress Bar and Counter */}
            <div className="mb-8">
                <div className="flex justify-between items-center mb-2">
                    <h2 className="text-lg font-semibold text-gray-700">Test Your Knowledge</h2>
                    <p className="text-sm text-gray-500">Question {currentQuizIndex + 1} of {quizzes.length}</p>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2.5">
                    <motion.div
                        className="bg-indigo-600 h-2.5 rounded-full"
                        style={{ width: `${progressPercentage}%` }}
                        initial={{ width: 0 }}
                        animate={{ width: `${progressPercentage}%` }}
                        transition={{ duration: 0.3 }}
                    />
                </div>
            </div>

            {/* Quiz Card */}
            <div className="bg-white p-8 rounded-xl shadow-2xl overflow-hidden">
                <AnimatePresence mode="wait">
                    <motion.div
                        key={currentQuizIndex}
                        variants={questionVariants}
                        initial="initial"
                        animate="animate"
                        exit="exit"
                        transition={{ duration: 0.3 }}
                    >
                        <h3 className="text-2xl font-bold text-gray-800 mb-6">{currentQuiz.question}</h3>
                        <div className="space-y-4">
                            {currentQuiz.options.map((option, index) => (
                                <button
                                    key={index}
                                    onClick={() => handleAnswerSelection(option)}
                                    disabled={isAnswered}
                                    className={`w-full text-left p-4 rounded-lg border-2 transition-all flex items-center justify-between ${getButtonClass(option)}`}
                                >
                                    <span className="font-medium">{option}</span>
                                    {isAnswered && option === currentQuiz.answer && <CheckIcon className="h-6 w-6 text-green-600" />}
                                    {isAnswered && selectedAnswer === option && option !== currentQuiz.answer && <XMarkIcon className="h-6 w-6 text-red-600" />}
                                </button>
                            ))}
                        </div>
                    </motion.div>
                </AnimatePresence>
            </div>

            {/* Action Button */}
            <div className="mt-6">
                <button
                    onClick={isAnswered ? handleNextQuestion : handleCheckAnswer}
                    disabled={!selectedAnswer}
                    className="w-full bg-indigo-600 text-white py-3 rounded-lg font-semibold hover:bg-indigo-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
                >
                    {isAnswered ? 'Next Question' : 'Check Answer'}
                </button>
            </div>
        </div>
    );
};

export default QuizzesPage;